"use client";

import { useTheaterOverlayFrozen } from "@/components/feature/theater-overlay-freeze";
import {
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type Testimonial = {
  quote: string;
  source: string;
};

const defaultTestimonials: Testimonial[] = [
  {
    quote: "My students absolutely adore PlayMath.org. They can't say enough good things about it!",
    source: "- K5 Teacher",
  },
  {
    quote:
      "Teachers found PlayMath.org more engaging than traditional math websites like Zearn and IXL learning",
    source: "- LeanLab Education Report",
  },
  {
    quote: "I'm so relieved to have my kids asking for an app that actually has them thinking!",
    source: "- Grade 3 & 7 Parent",
  },
  {
    quote: "Bro, come on! This game is making me want to come to school.",
    source: "- 5th Grade Student",
  },
  {
    quote: "They think they're playing a game, but it's actually math, and it's super engaging.",
    source: "- Brooke, K5 Teacher",
  },
  {
    quote: "There's no way this game is actually this fun.",
    source: "- 4th Grade Student",
  },
  {
    quote: "The games make students think and problem-solve instead of simply clicking through.",
    source: "- Holly, K5 Teacher",
  },
  {
    quote: "Can we play this again next class?",
    source: "- 5th Grade Student",
  },
  {
    quote: "I've tried countless websites and games. That's when I came across PlayMath.org.",
    source: "- Sam, K5 Teacher",
  },
  {
    quote: "We are so grateful for access to all of these games. My kids love them!",
    source: "- Parent",
  },
];

/**
 * Visible sag from viewport edge → center (px) on desktop.
 */
export const CLOTHESLINE_VISIBLE_SAG_PX = 48;
/** Shallower visible sag below the md breakpoint. */
export const CLOTHESLINE_VISIBLE_SAG_MOBILE_PX = 16;
/** Tailwind `md` width — below this we use the mobile sag. */
export const CLOTHESLINE_MD_BREAKPOINT_PX = 768;
/** Y of the line at the posts (px from curve origin). */
export const CLOTHESLINE_BASELINE_PX = 6;
/**
 * Card top sits this far *below* the rope (pins reach up to clamp on the wire).
 */
export const CARD_DROP_BELOW_LINE_PX = 10;
/** Clothespin stack height above the card top (tailwind -top-10). */
export const CARD_PIN_OVERHANG_PX = 40;
/** Steady cruise speed after idle / after a fling eases out (px/sec). Sign = direction. */
export const MARQUEE_CRUISE_SPEED_PX_PER_SEC = 36;
/** How quickly velocity eases toward cruise after a fling (higher = snappier). */
export const MARQUEE_VELOCITY_EASING_PER_SEC = 2.4;
/** Clamp fling speed so a wild throw doesn’t leave the marquee unreadable. */
export const MARQUEE_MAX_FLING_SPEED_PX_PER_SEC = 2200;
/** Autoplay stays frozen until the page has scrolled at least this far. */
export const MARQUEE_SCROLL_UNLOCK_PX = 64;
/** Px of movement before a touch gesture locks to horizontal (marquee) or vertical (page scroll). */
export const MARQUEE_AXIS_LOCK_THRESHOLD_PX = 8;
/**
 * Cap autoplay paint rate. Drag still updates every pointer move.
 * ~45fps is smoother than 30 while still cheaper than uncapped 60 on Chromebooks.
 */
export const MARQUEE_AUTOPLAY_MAX_FPS = 45;

export const CARD_GAP_PX = 40;
export const TRACK_PADDING_X_PX = 16;
/** Widest card width (matches md:w-[300px]). */
export const CARD_WIDTH_PX = 300;
/**
 * Only pose cards whose centers fall in [−margin, stage+margin].
 * Off-stage cards keep their last transform until they re-enter.
 */
export const MARQUEE_CARD_POSE_MARGIN_PX = CARD_WIDTH_PX;

/**
 * ARCHITECTURAL INVARIANT — do not weaken this.
 *
 * Hang Y is a parabola between posts that sit OUTSIDE the stage in *pixels*,
 * not a fraction of width. Posts are at least one full card width past each
 * edge, so every card center that can exist while a card is still partially
 * visible is strictly between the posts. Between the posts, Y is strictly
 * monotone from center → nearer post. That means hang Y can NEVER plateau at
 * a viewport edge at any breakpoint / stage width.
 *
 * Fractional overflow (e.g. 10% of width) is forbidden: on narrow screens the
 * plateau starts inside the card-travel zone and edge quotes stop rising.
 */
export const CLOTHESLINE_POST_OVERFLOW_PX = CARD_WIDTH_PX;

if (CLOTHESLINE_POST_OVERFLOW_PX < CARD_WIDTH_PX) {
  throw new Error(
    `[clothesline] CLOTHESLINE_POST_OVERFLOW_PX (${CLOTHESLINE_POST_OVERFLOW_PX}) ` +
      `must be >= CARD_WIDTH_PX (${CARD_WIDTH_PX}) so edge hang Y never plateaus.`
  );
}

/** Whether the marquee may autoplay given the current scroll position. */
export function isMarqueeAutoplayUnlocked(
  scrollY: number,
  unlockPx = MARQUEE_SCROLL_UNLOCK_PX
): boolean {
  return scrollY >= unlockPx;
}

/** Keep offset in [0, loopWidth). */
export function wrapMarqueeOffset(offset: number, loopWidth: number): number {
  if (loopWidth <= 0) return 0;
  return ((offset % loopWidth) + loopWidth) % loopWidth;
}

/**
 * How many copies of the quote list are needed so the viewport never shows a
 * gap before the loop wraps. Needs (copies - 1) * setWidth >= stageWidth.
 */
export function marqueeLoopCopyCount(setWidthPx: number, stageWidthPx: number): number {
  if (setWidthPx <= 0 || stageWidthPx <= 0) return 2;
  return Math.max(2, Math.ceil(stageWidthPx / setWidthPx) + 1);
}

/** Rough set width before layout (md card + gap). */
export function estimateMarqueeSetWidthPx(
  itemCount: number,
  cardWidthPx = CARD_WIDTH_PX,
  gapPx = CARD_GAP_PX
): number {
  if (itemCount <= 0) return 0;
  return itemCount * (cardWidthPx + gapPx);
}

/** Cruise speed matching the sign of the current/fling velocity (default = forward). */
export function cruiseSpeedForVelocity(
  velocityPxPerSec: number,
  cruiseSpeed = MARQUEE_CRUISE_SPEED_PX_PER_SEC
): number {
  if (Math.abs(velocityPxPerSec) < 1) return cruiseSpeed;
  return Math.sign(velocityPxPerSec) * Math.abs(cruiseSpeed);
}

/** Exponential ease of velocity toward directional cruise speed. */
export function stepMarqueeVelocity(
  velocityPxPerSec: number,
  dtSec: number,
  cruiseSpeed = MARQUEE_CRUISE_SPEED_PX_PER_SEC,
  easingPerSec = MARQUEE_VELOCITY_EASING_PER_SEC
): number {
  const target = cruiseSpeedForVelocity(velocityPxPerSec, cruiseSpeed);
  const alpha = 1 - Math.exp(-easingPerSec * Math.max(0, dtSec));
  const next = velocityPxPerSec + (target - velocityPxPerSec) * alpha;
  // Snap when close enough to avoid endless micro-drift.
  if (Math.abs(next - target) < 0.25) return target;
  return next;
}

export function clampMarqueeFlingSpeed(
  velocityPxPerSec: number,
  maxSpeed = MARQUEE_MAX_FLING_SPEED_PX_PER_SEC
): number {
  return Math.max(-maxSpeed, Math.min(maxSpeed, velocityPxPerSec));
}

export type MarqueeDragAxis = "pending" | "horizontal" | "vertical";

/** Decide whether a touch gesture is a horizontal marquee drag or vertical page scroll. */
export function resolveMarqueeDragAxis(
  dx: number,
  dy: number,
  thresholdPx = MARQUEE_AXIS_LOCK_THRESHOLD_PX
): MarqueeDragAxis {
  if (Math.max(Math.abs(dx), Math.abs(dy)) < thresholdPx) return "pending";
  return Math.abs(dx) >= Math.abs(dy) ? "horizontal" : "vertical";
}

/** True when a card center is close enough to the stage to need a fresh hang pose. */
export function isMarqueeCardNearStage(
  centerX: number,
  stageWidth: number,
  marginPx = MARQUEE_CARD_POSE_MARGIN_PX
): boolean {
  return centerX >= -marginPx && centerX <= stageWidth + marginPx;
}

/** Whether an autoplay tick should paint (FPS cap). Drag/force always paints. */
export function shouldPaintMarqueeFrame(
  nowMs: number,
  lastPaintMs: number | null,
  maxFps = MARQUEE_AUTOPLAY_MAX_FPS,
  force = false
): boolean {
  if (force || lastPaintMs == null || maxFps <= 0) return true;
  return nowMs - lastPaintMs >= 1000 / maxFps;
}

/** Gates for the marquee rAF loop (exported for unit tests). */
export function isMarqueeAutoplayActive(opts: {
  reducedMotion: boolean;
  loopWidth: number;
  dragging: boolean;
  scrollUnlocked: boolean;
  inView: boolean;
  tabVisible: boolean;
  overlayFrozen?: boolean;
}): boolean {
  return (
    !opts.reducedMotion &&
    opts.loopWidth > 0 &&
    !opts.dragging &&
    opts.scrollUnlocked &&
    opts.inView &&
    opts.tabVisible &&
    !opts.overlayFrozen
  );
}

export function isMarqueeDragActive(axis: MarqueeDragAxis | "idle", dragging: boolean): boolean {
  return dragging || axis === "pending" || axis === "horizontal";
}

/** Pixel X of the mathematical posts for a stage width. */
export function clotheslinePostXs(
  widthPx: number,
  overflowPx = CLOTHESLINE_POST_OVERFLOW_PX
): { left: number; right: number; span: number } {
  const left = -overflowPx;
  const right = widthPx + overflowPx;
  return { left, right, span: right - left };
}

/**
 * Normalized curve t at stage X. t=0/1 at posts (outside the stage).
 * Not clamped — callers that need a physical rope clamp explicitly.
 */
export function clotheslineCurveTAtX(
  x: number,
  widthPx: number,
  overflowPx = CLOTHESLINE_POST_OVERFLOW_PX
): number {
  const { left, span } = clotheslinePostXs(widthPx, overflowPx);
  if (span <= 0) return 0.5;
  return (x - left) / span;
}

/** Visible sag (edge → center) for the current stage width. */
export function clotheslineVisibleSagForWidth(widthPx: number): number {
  return widthPx < CLOTHESLINE_MD_BREAKPOINT_PX
    ? CLOTHESLINE_VISIBLE_SAG_MOBILE_PX
    : CLOTHESLINE_VISIBLE_SAG_PX;
}

/**
 * Full parabola sag (post → center) so (center Y − edge Y) === visible sag.
 * Depends on width because post overflow is a fixed pixel distance.
 */
export function clotheslineFullSagPx(
  visibleSagPx: number,
  widthPx: number,
  overflowPx = CLOTHESLINE_POST_OVERFLOW_PX
): number {
  const edgeT = clotheslineCurveTAtX(0, widthPx, overflowPx);
  // yFactor = 4 t (1-t); visible drop shares (centerFactor - edgeFactor) of full sag.
  const edgeFactor = 4 * edgeT * (1 - edgeT);
  const centerFactor = 1; // t=0.5
  const denom = centerFactor - edgeFactor;
  if (denom <= 0) {
    throw new Error(
      `[clothesline] invalid post geometry for width=${widthPx}: edge is not below center`
    );
  }
  return visibleSagPx / denom;
}

/** Full sag constant for a stage width (mobile is shallower). */
export function clotheslineSagPxForWidth(widthPx: number): number {
  return clotheslineFullSagPx(clotheslineVisibleSagForWidth(widthPx), widthPx);
}

/** Desktop reference full-sag (width 1000) for tests / defaults. */
export const CLOTHESLINE_SAG_PX = clotheslineSagPxForWidth(1000);

/**
 * Raw parabola drop from the posts (0 at posts, sagPx at center).
 * Not shifted into viewport space — use clotheslineYAtX for rendering.
 */
export function clotheslineRawDropAtX(
  x: number,
  widthPx: number,
  sagPx = clotheslineSagPxForWidth(widthPx),
  overflowPx = CLOTHESLINE_POST_OVERFLOW_PX
): number {
  const t = clotheslineCurveTAtX(x, widthPx, overflowPx);
  const tc = Math.min(1, Math.max(0, t));
  return sagPx * 4 * tc * (1 - tc);
}

/**
 * Clothesline Y at a stage-pixel X (same curve the SVG and cards share).
 *
 * Viewport edges are normalized to `baselinePx` so far-out posts (required for
 * the no-plateau invariant) don't push the visible rope down into a huge empty
 * band under the hero. Past the edges, Y continues upward (below baseline).
 */
export function clotheslineYAtX(
  x: number,
  widthPx: number,
  sagPx = clotheslineSagPxForWidth(widthPx),
  baselinePx = CLOTHESLINE_BASELINE_PX,
  overflowPx = CLOTHESLINE_POST_OVERFLOW_PX
): number {
  const raw = clotheslineRawDropAtX(x, widthPx, sagPx, overflowPx);
  const rawAtViewportEdge = clotheslineRawDropAtX(0, widthPx, sagPx, overflowPx);
  return baselinePx + (raw - rawAtViewportEdge);
}

/** @deprecated Prefer clotheslineYAtX — kept as a viewport-t convenience. */
export function clotheslineY(
  viewportT: number,
  widthPx = 1000,
  sagPx = clotheslineSagPxForWidth(widthPx),
  baselinePx = CLOTHESLINE_BASELINE_PX
): number {
  return clotheslineYAtX(viewportT * widthPx, widthPx, sagPx, baselinePx);
}

/** dy/dx (px per px) at stage-pixel X. */
export function clotheslineSlopeAtX(
  x: number,
  widthPx: number,
  sagPx = clotheslineSagPxForWidth(widthPx),
  overflowPx = CLOTHESLINE_POST_OVERFLOW_PX
): number {
  const { span } = clotheslinePostXs(widthPx, overflowPx);
  if (span <= 0) return 0;
  const t = clotheslineCurveTAtX(x, widthPx, overflowPx);
  const tc = Math.min(1, Math.max(0, t));
  return (sagPx * 4 * (1 - 2 * tc)) / span;
}

/** @deprecated Prefer clotheslineSlopeAtX. */
export function clotheslineSlopePerPx(
  viewportT: number,
  widthPx: number,
  sagPx = clotheslineSagPxForWidth(widthPx)
): number {
  return clotheslineSlopeAtX(viewportT * widthPx, widthPx, sagPx);
}

/** SVG path `d` from the same curve the cards use. */
export function buildClotheslinePathD(
  widthPx: number,
  sagPx = clotheslineSagPxForWidth(widthPx),
  baselinePx = CLOTHESLINE_BASELINE_PX,
  samples = 64
): string {
  if (widthPx <= 0) return "";
  const parts: string[] = [];
  for (let i = 0; i <= samples; i++) {
    const x = (i / samples) * widthPx;
    const y = clotheslineYAtX(x, widthPx, sagPx, baselinePx);
    parts.push(`${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`);
  }
  return parts.join(" ");
}

/** Pose for a card whose hang point is at stage X = centerX. */
export function cardPoseOnClothesline(
  centerX: number,
  stageWidth: number,
  sagPx = clotheslineSagPxForWidth(stageWidth)
): { y: number; angleDeg: number; viewportT: number; lineY: number } {
  const lineY = clotheslineYAtX(centerX, stageWidth, sagPx);
  const angleDeg = (Math.atan(clotheslineSlopeAtX(centerX, stageWidth, sagPx)) * 180) / Math.PI;
  return {
    y: lineY + CARD_DROP_BELOW_LINE_PX,
    angleDeg,
    viewportT: stageWidth > 0 ? centerX / stageWidth : 0,
    lineY,
  };
}

/**
 * True when hang Y is strictly monotone from mid → outward across the entire
 * card-travel zone for this width. This is the invariant that must hold at
 * every breakpoint — if it fails, edge quotes will stop rising again.
 */
export function clotheslineHangYIsStrictlyRisingOutward(stageWidth: number, stepPx = 1): boolean {
  if (stageWidth <= 0) return false;
  // Card centers while a card is still partially visible: roughly ± half card
  // past each edge. Use full card width for a hard safety margin.
  const travelLeft = -CARD_WIDTH_PX;
  const travelRight = stageWidth + CARD_WIDTH_PX;
  const mid = stageWidth / 2;

  for (let x = mid; x >= travelLeft + stepPx; x -= stepPx) {
    const outer = cardPoseOnClothesline(x - stepPx, stageWidth).y;
    const inner = cardPoseOnClothesline(x, stageWidth).y;
    if (!(outer < inner)) return false;
  }
  for (let x = mid; x <= travelRight - stepPx; x += stepPx) {
    const outer = cardPoseOnClothesline(x + stepPx, stageWidth).y;
    const inner = cardPoseOnClothesline(x, stageWidth).y;
    if (!(outer < inner)) return false;
  }
  return true;
}

/**
 * Tight top padding: enough for pins/tilt at the viewport edge, small enough
 * that the clothesline peeks under the hero at scroll-top.
 */
export function clotheslineStagePaddingTopPx(stageWidth = 1000): number {
  const edgePose = cardPoseOnClothesline(0, stageWidth);
  const pinTop = edgePose.y - CARD_PIN_OVERHANG_PX;
  const tiltLiftPx = Math.sin(Math.abs(edgePose.angleDeg) * (Math.PI / 180)) * (CARD_WIDTH_PX / 2);
  return Math.ceil(Math.max(0, -pinTop) + tiltLiftPx + 8);
}

/**
 * Bottom padding so transformed quote cards fit inside the stage.
 * Without this, overflow-x clipping promotes overflow-y to scroll and the
 * marquee becomes its own scroll container (nested scroll on mobile).
 */
export function clotheslineStagePaddingBottomPx(stageWidth = 1000): number {
  const centerPose = cardPoseOnClothesline(stageWidth / 2, stageWidth);
  const tiltDropPx =
    Math.sin(Math.abs(centerPose.angleDeg) * (Math.PI / 180)) * (CARD_WIDTH_PX / 2);
  // Layout already includes the untransformed card height; padding only needs
  // to cover the downward translate + tilt + a little breathing room.
  return Math.ceil(centerPose.y + tiltDropPx + 56);
}

/** Delay before the clothesline slides up into its resting place. */
export const QUOTES_INTRO_DELAY_MS = 2800;
/** Slide-up duration after the intro delay. */
export const QUOTES_INTRO_SLIDE_MS = 900;
/** How far below rest the quotes start (viewport-relative). */
export const QUOTES_INTRO_OFFSET_VH = 10;

type TestimonialsCarouselProps = {
  testimonials?: Testimonial[];
  showStars?: boolean;
  /**
   * When true (default), clothesline starts below rest and slides up after a delay.
   * Home/about hero timing; disable on other pages (e.g. educators).
   */
  enableIntroSlide?: boolean;
};

export function TestimonialsCarousel({
  testimonials = defaultTestimonials,
  showStars = true,
  enableIntroSlide = true,
}: TestimonialsCarouselProps) {
  // false until the intro timer (or reduced-motion) settles the clothesline.
  const [introSettled, setIntroSettled] = useState(!enableIntroSlide);

  useEffect(() => {
    if (!enableIntroSlide) {
      setIntroSettled(true);
      return;
    }
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) {
      setIntroSettled(true);
      return;
    }
    const id = window.setTimeout(() => setIntroSettled(true), QUOTES_INTRO_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [enableIntroSlide]);

  return (
    // No top padding — hero height leaves a peek; clothesline should sit in that band.
    // overflow-clip (both axes): overflow-x-hidden alone forces overflow-y:auto and
    // creates a nested scrollport when cards translate below the stage.
    <section
      className="pt-0 pb-16 md:pb-28 max-w-full overflow-clip -mt-4 md:mt-0 will-change-transform"
      data-testid="testimonials-section"
      data-intro-settled={introSettled ? "true" : "false"}
      style={{
        transform: introSettled
          ? "translate3d(0, 0, 0)"
          : `translate3d(0, ${QUOTES_INTRO_OFFSET_VH}vh, 0)`,
        transition: enableIntroSlide
          ? `transform ${QUOTES_INTRO_SLIDE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
          : undefined,
      }}
    >
      <TestimonialsMarquee testimonials={testimonials} showStars={showStars} />
    </section>
  );
}

function TestimonialsMarquee({
  testimonials,
  showStars,
}: {
  testimonials: Testimonial[];
  showStars: boolean;
}) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const slotRefs = useRef<Array<HTMLElement | null>>([]);
  const cardRefs = useRef<Array<HTMLElement | null>>([]);
  /** Track-local card-center X, measured on layout — not every frame. */
  const slotCenterXRef = useRef<Float64Array>(new Float64Array(0));
  const pathRef = useRef<SVGPathElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const offsetRef = useRef(0);
  const velocityRef = useRef(MARQUEE_CRUISE_SPEED_PX_PER_SEC);
  const draggingRef = useRef(false);
  const dragAxisRef = useRef<MarqueeDragAxis | "idle">("idle");
  const dragStartXRef = useRef(0);
  const dragStartYRef = useRef(0);
  const dragLastXRef = useRef(0);
  const dragLastTsRef = useRef(0);
  const loopWidthRef = useRef(0);
  const stageWidthRef = useRef(0);
  const stageSagPxRef = useRef(CLOTHESLINE_SAG_PX);
  const setWidthRef = useRef(0);
  const copiesRef = useRef(2);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);
  const lastPaintTsRef = useRef<number | null>(null);
  const reducedMotionRef = useRef(false);
  const autoplayUnlockedRef = useRef(false);
  /** Assume on-screen until IntersectionObserver says otherwise (or is unavailable). */
  const inViewRef = useRef(true);
  const tabVisibleRef = useRef(true);
  const overlayFrozen = useTheaterOverlayFrozen();
  const overlayFrozenRef = useRef(overlayFrozen);
  overlayFrozenRef.current = overlayFrozen;
  const ensureTickRef = useRef<() => void>(() => {});
  const setCount = testimonials.length;

  // Always start at 2 so SSR HTML matches the first client paint. Extra copies
  // for short lists are applied after mount in `measure()` (avoids hydration mismatch
  // from reading `window.innerWidth` during useState init).
  const [copies, setCopies] = useState(2);
  copiesRef.current = copies;

  const loopItems = useMemo(() => {
    const items: Testimonial[] = [];
    for (let i = 0; i < copies; i++) items.push(...testimonials);
    return items;
  }, [testimonials, copies]);
  const paddingTop = clotheslineStagePaddingTopPx();
  const paddingBottom = clotheslineStagePaddingBottomPx();

  const applyTrackTransform = () => {
    const track = trackRef.current;
    if (!track) return;
    track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
  };

  /** Cache slot centers after layout/resize — reading offsetLeft every frame was still costly. */
  const cacheSlotCenters = () => {
    const slots = slotRefs.current;
    const centers = new Float64Array(slots.length);
    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i];
      if (!slot) {
        centers[i] = Number.NaN;
        continue;
      }
      centers[i] = slot.offsetLeft + slot.offsetWidth / 2;
    }
    slotCenterXRef.current = centers;
  };

  /**
   * Pose near-stage cards from cached track geometry + current marquee offset.
   * Skips off-stage cards and avoids per-frame layout reads.
   */
  const placeCardsOnCurve = () => {
    const stageWidth = stageWidthRef.current;
    if (stageWidth <= 0) return;
    const offset = offsetRef.current;
    const sagPx = stageSagPxRef.current;
    const centers = slotCenterXRef.current;
    const cards = cardRefs.current;
    const n = Math.min(centers.length, cards.length);

    for (let i = 0; i < n; i++) {
      const card = cards[i];
      const centerInTrack = centers[i];
      if (!card || Number.isNaN(centerInTrack)) continue;

      const centerX = centerInTrack - offset;
      if (!isMarqueeCardNearStage(centerX, stageWidth)) continue;

      const pose = cardPoseOnClothesline(centerX, stageWidth, sagPx);
      card.style.transform = `translate3d(0, ${pose.y}px, 0) rotate(${pose.angleDeg}deg)`;
    }
  };

  /** One set’s width: distance from the first card to the start of the next copy. */
  const measureSetWidth = () => {
    if (setCount <= 0) return 0;
    const first = slotRefs.current[0];
    const nextCopy = slotRefs.current[setCount];
    if (first && nextCopy) {
      const delta = nextCopy.offsetLeft - first.offsetLeft;
      if (delta > 0) return delta;
    }
    // Fallback when layout hasn’t produced offsetLeft yet (e.g. jsdom).
    const track = trackRef.current;
    if (track && copiesRef.current > 0) {
      const trackWidth = track.scrollWidth;
      if (trackWidth > 0) return trackWidth / copiesRef.current;
    }
    return estimateMarqueeSetWidthPx(setCount);
  };

  const ensureLoopWidth = () => {
    if (loopWidthRef.current <= 0) {
      const setWidth = measureSetWidth();
      setWidthRef.current = setWidth;
      loopWidthRef.current = setWidth;
    }
    return loopWidthRef.current;
  };

  const beginHorizontalDrag = (stage: HTMLDivElement, pointerId: number) => {
    dragAxisRef.current = "horizontal";
    draggingRef.current = true;
    velocityRef.current = 0;
    stage.dataset.dragging = "true";
    stage.style.touchAction = "none";
    try {
      stage.setPointerCapture(pointerId);
    } catch {
      // jsdom / unsupported environments
    }
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    // Ignore non-primary mouse buttons; touch/pen have button 0.
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const stage = stageRef.current;
    if (!stage) return;
    ensureLoopWidth();
    dragStartXRef.current = e.clientX;
    dragStartYRef.current = e.clientY;
    dragLastXRef.current = e.clientX;
    dragLastTsRef.current = e.timeStamp;

    // Touch/pen: wait to see if the gesture is horizontal so vertical scrolls
    // aren't captured. Mouse (and unknown pointer types) grab immediately.
    if (e.pointerType === "touch" || e.pointerType === "pen") {
      dragAxisRef.current = "pending";
      draggingRef.current = false;
    } else {
      beginHorizontalDrag(stage, e.pointerId);
    }
    ensureTickRef.current();
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const stage = stageRef.current;
    if (!stage) return;

    if (dragAxisRef.current === "pending") {
      const axis = resolveMarqueeDragAxis(
        e.clientX - dragStartXRef.current,
        e.clientY - dragStartYRef.current
      );
      if (axis === "pending") return;
      if (axis === "vertical") {
        // Let the browser handle page scroll; never capture this gesture.
        dragAxisRef.current = "vertical";
        return;
      }
      // Keep lastX at the pointer-down position so this move applies the full dx.
      beginHorizontalDrag(stage, e.pointerId);
    }

    if (dragAxisRef.current !== "horizontal" || !draggingRef.current) return;

    const loopWidth = ensureLoopWidth();
    const dx = e.clientX - dragLastXRef.current;
    const dtMs = e.timeStamp - dragLastTsRef.current;
    dragLastXRef.current = e.clientX;
    dragLastTsRef.current = e.timeStamp;

    offsetRef.current = wrapMarqueeOffset(offsetRef.current - dx, loopWidth);
    applyTrackTransform();
    placeCardsOnCurve();

    if (dtMs > 0) {
      velocityRef.current = clampMarqueeFlingSpeed((-dx / dtMs) * 1000);
    }
  };

  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    const stage = stageRef.current;
    const wasHorizontal = dragAxisRef.current === "horizontal" && draggingRef.current;
    dragAxisRef.current = "idle";
    draggingRef.current = false;
    if (stage) {
      delete stage.dataset.dragging;
      stage.style.touchAction = "";
    }
    try {
      stage?.releasePointerCapture(e.pointerId);
    } catch {
      // already released
    }
    if (!wasHorizontal) return;
    if (Math.abs(velocityRef.current) < 40) {
      velocityRef.current = MARQUEE_CRUISE_SPEED_PX_PER_SEC;
    } else {
      velocityRef.current = clampMarqueeFlingSpeed(velocityRef.current);
    }
    // Resume autoplay loop after drag (may have been idle before pointer-down).
    ensureTickRef.current();
  };

  useEffect(() => {
    const stage = stageRef.current;
    const track = trackRef.current;
    const path = pathRef.current;
    const svg = svgRef.current;
    if (!stage || !track || !path || !svg) return;

    reducedMotionRef.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const updateAutoplayGate = () => {
      autoplayUnlockedRef.current = isMarqueeAutoplayUnlocked(window.scrollY);
    };

    const measure = () => {
      const stageWidth = stage.clientWidth || window.innerWidth;
      stageWidthRef.current = stageWidth;

      const setWidth = measureSetWidth();
      setWidthRef.current = setWidth;
      loopWidthRef.current = setWidth;

      // Short quote lists need extra copies so the viewport never empties mid-loop.
      const needed = marqueeLoopCopyCount(setWidth, stageWidth);
      if (needed !== copiesRef.current) {
        setCopies(needed);
      }

      const sagPx = clotheslineSagPxForWidth(stageWidth);
      stageSagPxRef.current = sagPx;
      const visibleSag = clotheslineVisibleSagForWidth(stageWidth);
      // Path is viewport-normalized: edges at baseline, center at baseline+visibleSag.
      const svgHeight = CLOTHESLINE_BASELINE_PX + visibleSag + 8;
      svg.setAttribute("viewBox", `0 0 ${stageWidth} ${svgHeight}`);
      svg.style.height = `${svgHeight}px`;
      path.setAttribute("d", buildClotheslinePathD(stageWidth, sagPx));

      const nextPaddingTop = clotheslineStagePaddingTopPx(stageWidth);
      const nextPaddingBottom = clotheslineStagePaddingBottomPx(stageWidth);
      stage.style.paddingTop = `${nextPaddingTop}px`;
      stage.style.paddingBottom = `${nextPaddingBottom}px`;
      svg.style.top = `${nextPaddingTop}px`;

      cacheSlotCenters();
    };

    const tick = (ts: number) => {
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = Math.min(0.05, (ts - lastTsRef.current) / 1000);
      lastTsRef.current = ts;

      const loopWidth = loopWidthRef.current;
      const dragging = isMarqueeDragActive(dragAxisRef.current, draggingRef.current);
      const canAutoplay = isMarqueeAutoplayActive({
        reducedMotion: reducedMotionRef.current,
        loopWidth,
        dragging: draggingRef.current,
        scrollUnlocked: autoplayUnlockedRef.current,
        inView: inViewRef.current,
        tabVisible: tabVisibleRef.current,
        overlayFrozen: overlayFrozenRef.current,
      });

      if (canAutoplay) {
        // Always integrate motion so FPS cap doesn't change travel distance.
        velocityRef.current = stepMarqueeVelocity(velocityRef.current, dt);
        offsetRef.current = wrapMarqueeOffset(
          offsetRef.current + velocityRef.current * dt,
          loopWidth
        );
        if (shouldPaintMarqueeFrame(ts, lastPaintTsRef.current)) {
          lastPaintTsRef.current = ts;
          applyTrackTransform();
          placeCardsOnCurve();
        }
      }

      // Idle when locked / off-screen / reduced-motion / not dragging — stop burning frames.
      if (canAutoplay || dragging) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
        lastTsRef.current = null;
        lastPaintTsRef.current = null;
      }
    };

    const ensureTick = () => {
      if (rafRef.current != null) return;
      lastTsRef.current = null;
      lastPaintTsRef.current = null;
      rafRef.current = requestAnimationFrame(tick);
    };
    ensureTickRef.current = ensureTick;

    measure();
    updateAutoplayGate();
    tabVisibleRef.current = !document.hidden;
    placeCardsOnCurve();
    applyTrackTransform();

    const ro = new ResizeObserver(() => {
      measure();
      placeCardsOnCurve();
    });
    ro.observe(stage);

    let io: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const next = Boolean(entry?.isIntersecting);
          const was = inViewRef.current;
          inViewRef.current = next;
          if (!was && next) ensureTick();
        },
        // Start work a bit before the section enters the viewport.
        { root: null, rootMargin: "120px 0px", threshold: 0 }
      );
      io.observe(stage);
    }

    const onVisibility = () => {
      const next = !document.hidden;
      const was = tabVisibleRef.current;
      tabVisibleRef.current = next;
      if (!was && next) ensureTick();
    };
    document.addEventListener("visibilitychange", onVisibility);

    const onScrollGate = () => {
      const wasUnlocked = autoplayUnlockedRef.current;
      updateAutoplayGate();
      if (!wasUnlocked && autoplayUnlockedRef.current) ensureTick();
    };
    window.addEventListener("scroll", onScrollGate, { passive: true });

    // Start the loop only when it can do useful work.
    if (
      autoplayUnlockedRef.current &&
      !reducedMotionRef.current &&
      inViewRef.current &&
      tabVisibleRef.current &&
      !overlayFrozenRef.current
    ) {
      ensureTick();
    }

    return () => {
      ro.disconnect();
      io?.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("scroll", onScrollGate);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTsRef.current = null;
      lastPaintTsRef.current = null;
      ensureTickRef.current = () => {};
    };
  }, [testimonials.length, copies, setCount]);

  useEffect(() => {
    if (overlayFrozen) {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastTsRef.current = null;
      lastPaintTsRef.current = null;
      return;
    }
    ensureTickRef.current();
  }, [overlayFrozen]);

  return (
    <div
      ref={stageRef}
      className="relative w-full overflow-clip cursor-grab data-[dragging=true]:cursor-grabbing select-none touch-pan-y"
      style={{ paddingTop, paddingBottom }}
      data-testid="testimonials-marquee"
      data-padding-top={paddingTop}
      data-padding-bottom={paddingBottom}
      data-copies={copies}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <svg
        ref={svgRef}
        className="pointer-events-none absolute left-0 z-10 w-full"
        style={{ top: paddingTop }}
        role="img"
        aria-label="Clothesline"
        preserveAspectRatio="none"
      >
        <path
          ref={pathRef}
          fill="none"
          style={{ stroke: "rgb(var(--brand-purple-black))" }}
          strokeWidth="6"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      <div
        ref={trackRef}
        className="relative z-20 flex w-max will-change-transform"
        style={{
          gap: CARD_GAP_PX,
          paddingLeft: TRACK_PADDING_X_PX,
          paddingRight: TRACK_PADDING_X_PX,
        }}
        data-testid="testimonials-marquee-track"
      >
        {loopItems.map((testimonial, index) => (
          <div
            key={`${testimonial.source}-${index}`}
            ref={(el) => {
              slotRefs.current[index] = el;
            }}
            className="w-[260px] sm:w-[280px] md:w-[300px] shrink-0"
            data-testid="testimonial-slot"
          >
            <ClotheslineCard
              testimonial={testimonial}
              showStars={showStars}
              cardRef={(el) => {
                cardRefs.current[index] = el;
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function ClotheslineCard({
  testimonial,
  showStars,
  cardRef,
}: {
  testimonial: Testimonial;
  showStars: boolean;
  cardRef: (el: HTMLElement | null) => void;
}) {
  return (
    <article
      ref={cardRef}
      className="relative w-full origin-top"
      aria-label={`Testimonial ${testimonial.source}`}
    >
      <div className="absolute -top-10 left-2 z-30 pointer-events-none">
        <img
          src="/images/marketing/home/testimonials/clothespin.svg"
          alt=""
          aria-hidden="true"
          className="w-6 h-16 -rotate-[20deg]"
        />
      </div>
      <div className="absolute -top-10 right-2 z-30 pointer-events-none">
        <img
          src="/images/marketing/home/testimonials/clothespin.svg"
          alt=""
          aria-hidden="true"
          className="w-6 h-16 rotate-[20deg]"
        />
      </div>

      <div className="bg-surface-lavender border-2 border-black rounded-3xl p-6 md:p-7 text-center relative z-20 h-full">
        <div className="flex justify-center mb-4 md:mb-5">
          <img
            src="/images/marketing/home/testimonials/quote.svg"
            alt=""
            aria-hidden="true"
            className="w-9 h-7 md:w-11 md:h-8"
          />
        </div>
        <p className="text-black font-bold text-base md:text-lg mb-3 md:mb-4 text-pretty">
          {testimonial.quote}
        </p>
        <p
          className={`text-black/50 font-normal text-xs md:text-sm ${
            showStars ? "mb-4 md:mb-5" : "mb-0"
          }`}
        >
          {testimonial.source}
        </p>
        {showStars ? (
          <div className="flex justify-center gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <img
                key={i}
                src="/images/marketing/home/testimonials/star.svg"
                alt=""
                aria-hidden="true"
                className="w-5 h-5 md:w-6 md:h-6"
              />
            ))}
          </div>
        ) : null}
      </div>
    </article>
  );
}
